import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

export function LiveDriverMap({ drivers = [], orders = [], region }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef({});
  const [mapLoaded, setMapLoaded] = useState(false);

  // Build unified marker list: every ONLINE driver with a known location.
  // If a driver is actively delivering, prefer the live order.driver_location
  // (updates every 5s) and attach order details.
  const orderByDriver = {};
  orders.forEach(o => {
    if (o.status === 'out_for_delivery' && o.driver_id && o.driver_location) {
      orderByDriver[o.driver_id] = o;
    }
  });

  const mapDrivers = drivers
    // Feb 2026 — include drivers with an active delivery even if their
    // `online` flag is briefly stale (the order itself is proof of life).
    // Closes the dispatch-map UX bug where Beejay was "1 On Route" in the
    // counter but "NO DRIVERS ONLINE" in the map body, because his iOS
    // PWA had been backgrounded long enough to drift past the freshness
    // window mid-delivery.
    .filter(d => {
      const onActiveRoute = Boolean(orderByDriver[d.id]);
      const isLive = d.online || d.has_active_route || onActiveRoute;
      const loc = orderByDriver[d.id]?.driver_location || d.current_location;
      return isLive && loc;
    })
    .map(d => {
      const order = orderByDriver[d.id];
      const location = order?.driver_location || d.current_location;
      return {
        id: d.id,
        name: d.name,
        location,
        status: order ? 'delivering' : 'idle',
        orderId: order?.id,
        customerName: order?.customer_name,
        deliveryAddress: order?.delivery_address
      };
    });

  useEffect(() => {
    if (map.current) return; // Initialize map only once

    // Feb 2026 — region-aware default center. Previously the map opened at
    // Denver, CO (a hardcoded placeholder unrelated to Ash & Merch's service
    // area). We now pick a region-appropriate center so the operator sees
    // their delivery area immediately. If we have ANY driver/order with a
    // location below, the `fitBounds` effect further down will snap to the
    // actual data — this default only matters for the empty-state.
    const REGION_CENTERS = {
      dmv: [-77.0369, 38.9072],  // Washington, DC
      nj:  [-74.4407, 40.0583],  // Trenton, NJ (center of state)
    };
    const defaultCenter = REGION_CENTERS[(region || '').toLowerCase()] || [-77.0369, 38.9072];

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: defaultCenter,
      zoom: 10,
    });

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Feb 2026 — "Center on Region" button. Custom Mapbox IControl so it
    // sits flush with the built-in zoom/compass buttons. Useful when the
    // operator has panned away from their region or is in super-admin mode
    // viewing both regions and wants to snap back to a known centre.
    class CenterOnRegionControl {
      onAdd(mapInstance) {
        this._map = mapInstance;
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.title = 'Center on region';
        btn.setAttribute('data-testid', 'map-center-on-region');
        btn.innerHTML =
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
          'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;">' +
          '<circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="3"></circle>' +
          '<line x1="12" y1="2" x2="12" y2="5"></line><line x1="12" y1="19" x2="12" y2="22"></line>' +
          '<line x1="2" y1="12" x2="5" y2="12"></line><line x1="19" y1="12" x2="22" y2="12"></line></svg>';
        btn.onclick = () => {
          this._map.easeTo({ center: defaultCenter, zoom: 10, duration: 600 });
        };
        this._container.appendChild(btn);
        return this._container;
      }
      onRemove() {
        if (this._container && this._container.parentNode) {
          this._container.parentNode.removeChild(this._container);
        }
        this._map = undefined;
      }
    }
    map.current.addControl(new CenterOnRegionControl(), 'top-right');

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // intentionally empty — map is initialized once. Region re-centring
           // happens via the fitBounds effect below when data arrives.

  // Feb 2026 — Auto-fit the viewport to the actual driver + delivery
  // positions so the operator never has to manually pan from the default
  // region centre. Only runs ONCE per "fresh batch of data" — we don't
  // want to yank the map every 30s tick when the operator is mid-pan.
  const hasFitted = useRef(false);
  useEffect(() => {
    if (!mapLoaded || !map.current || hasFitted.current) return;
    if (mapDrivers.length === 0) return;
    const coords = mapDrivers
      .map(d => d.location)
      .filter(loc => loc && typeof loc.lng === 'number' && typeof loc.lat === 'number');
    if (coords.length === 0) return;
    const bounds = new mapboxgl.LngLatBounds();
    coords.forEach(loc => bounds.extend([loc.lng, loc.lat]));
    map.current.fitBounds(bounds, { padding: 60, maxZoom: 13, duration: 700 });
    hasFitted.current = true;
  }, [mapLoaded, mapDrivers]);

  // Update markers when driver list changes
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    // Phase 7-lite — XSS sanitizer for dynamic strings interpolated into the
    // marker / popup HTML. Driver names, customer names, addresses ALL come
    // from user input and would otherwise execute in the dispatcher's browser.
    const escapeHtml = (s) => {
      if (s == null) return '';
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };

    // Remove stale markers
    Object.keys(markers.current).forEach(driverId => {
      if (!mapDrivers.find(d => d.id === driverId)) {
        markers.current[driverId].remove();
        delete markers.current[driverId];
      }
    });

    mapDrivers.forEach(driver => {
      const { id, name, location, status, customerName, deliveryAddress } = driver;
      const safeName = escapeHtml(name || '');
      const safeCustomerName = escapeHtml(customerName || '');
      const safeAddress = escapeHtml(deliveryAddress || '');
      const color = status === 'delivering' ? '#39FF14' : '#00F0FF';
      const glow = status === 'delivering' ? 'rgba(57,255,20,0.6)' : 'rgba(0,240,255,0.5)';
      const label = status === 'delivering' ? 'ON ROUTE' : 'IDLE';

      if (markers.current[id]) {
        // Update existing marker position + styling (rebuild if status changed)
        const existing = markers.current[id];
        const elStatus = existing._statusCache;
        if (elStatus === status) {
          existing.setLngLat([location.lng, location.lat]);
          return;
        }
        existing.remove();
        delete markers.current[id];
      }

      const el = document.createElement('div');
      el.className = 'driver-marker';
      el.innerHTML = `
        <div style="
          background: ${color};
          color: #050003;
          padding: 4px 8px;
          border-radius: 0;
          font-family: 'JetBrains Mono', monospace;
          font-size: 10px;
          font-weight: bold;
          white-space: nowrap;
          box-shadow: 0 0 12px ${glow};
          display: flex;
          align-items: center;
          gap: 4px;
        ">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="1" y="3" width="15" height="13"/>
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
            <circle cx="5.5" cy="18.5" r="2.5"/>
            <circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
          ${safeName.split(' ')[0].toUpperCase()}
          <span style="opacity:0.85;">· ${label}</span>
        </div>
      `;

      const popupHtml = status === 'delivering' ? `
        <div style="background:#0C0612;color:#39FF14;padding:12px;font-family:'JetBrains Mono',monospace;font-size:11px;border:1px solid #39FF14;">
          <div style="font-weight:bold;margin-bottom:6px;">${safeName}</div>
          <div style="color:#A3C7A8;margin-bottom:4px;">On route · ${safeCustomerName}</div>
          <div style="color:#6B8E73;font-size:10px;">${safeAddress}</div>
        </div>
      ` : `
        <div style="background:#0C0612;color:#00F0FF;padding:12px;font-family:'JetBrains Mono',monospace;font-size:11px;border:1px solid #00F0FF;">
          <div style="font-weight:bold;margin-bottom:6px;">${safeName}</div>
          <div style="color:#A3C7A8;">IDLE · Available for assignment</div>
        </div>
      `;

      const popup = new mapboxgl.Popup({ offset: 25, className: 'driver-popup' }).setHTML(popupHtml);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([location.lng, location.lat])
        .setPopup(popup)
        .addTo(map.current);
      marker._statusCache = status;
      markers.current[id] = marker;
    });

    // Fit bounds to show all markers (only when we have some)
    if (mapDrivers.length > 0) {
      const bounds = new mapboxgl.LngLatBounds();
      mapDrivers.forEach(d => bounds.extend([d.location.lng, d.location.lat]));
      map.current.fitBounds(bounds, { padding: 60, maxZoom: 14 });
    }
  }, [mapDrivers, mapLoaded]);

  const idleCount = mapDrivers.filter(d => d.status === 'idle').length;
  const routeCount = mapDrivers.filter(d => d.status === 'delivering').length;

  return (
    <div className="relative">
      <div
        ref={mapContainer}
        className="w-full h-[440px] border border-[#39FF14]"
        style={{ borderRadius: 0 }}
        data-testid="live-driver-map"
      />
      {mapDrivers.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#050003]/80 pointer-events-none">
          <div className="text-center">
            <p className="text-[#39FF14] font-mono text-sm">// NO DRIVERS ONLINE</p>
            <p className="text-[#4B6B52] font-mono text-xs mt-1">Drivers appear here the moment they log in</p>
          </div>
        </div>
      )}
      <div className="absolute bottom-4 left-4 bg-[#0C0612] border border-[#2A0F3D] p-3">
        <p className="text-[#6B8E73] font-mono text-xs uppercase mb-2">LEGEND</p>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#39FF14]" />
          <span className="text-[#A3C7A8] font-mono text-xs">On Route ({routeCount})</span>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="w-3 h-3 bg-[#00F0FF]" />
          <span className="text-[#A3C7A8] font-mono text-xs">Idle / Available ({idleCount})</span>
        </div>
      </div>
    </div>
  );
}
